import { useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

function getScoreColor(score: number, selected: boolean): string {
  const base = selected ? 'ring-4 ring-offset-2 scale-110 ' : '';
  if (score <= 6) return base + 'bg-red-500 text-white hover:bg-red-600';
  if (score <= 8) return base + 'bg-yellow-400 text-gray-900 hover:bg-yellow-500';
  return base + 'bg-green-500 text-white hover:bg-green-600';
}

function parseErrorMessage(message: string): string {
  if (message.includes('já respondida')) return 'Esta pesquisa já foi respondida.';
  if (message.includes('expirada')) return 'Este link expirou. Entre em contato com a clínica.';
  if (message.includes('não encontrada') || message.includes('não encontrado')) return 'Link inválido.';
  return 'Ocorreu um erro. Tente novamente ou entre em contato com a clínica.';
}

export function NpsRespondPage() {
  const { token } = useParams<{ token: string }>();
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async () => {
    if (selectedScore === null || !token) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch(`${API_URL}/api/v1/nps/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          score: selectedScore,
          comment: comment.trim() || undefined,
        }),
      });

      if (res.ok) {
        setStatus('success');
        return;
      }

      const body = await res.json().catch(() => ({}));
      const msg = body?.message ?? 'Erro desconhecido';
      setErrorMessage(parseErrorMessage(msg));
      setStatus('error');
    } catch {
      setErrorMessage('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
      setStatus('error');
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="text-5xl mb-4">🙏</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Obrigado pelo seu feedback!</h1>
          <p className="text-gray-500 text-sm">Sua avaliação foi registrada e nos ajudará a melhorar cada vez mais.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-3xl mb-3">⭐</div>
          <h1 className="text-lg font-semibold text-gray-900 leading-snug">
            De 0 a 10, quanto você indicaria nossa clínica para um amigo ou familiar?
          </h1>
        </div>

        {/* Score selector */}
        <div className="mb-2">
          <div className="grid grid-cols-11 gap-1">
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                onClick={() => setSelectedScore(i)}
                disabled={status === 'submitting'}
                className={[
                  'aspect-square rounded-lg text-sm font-bold transition-all duration-100 focus:outline-none disabled:opacity-50',
                  getScoreColor(i, selectedScore === i),
                  selectedScore !== i ? 'opacity-80' : '',
                ].join(' ')}
                aria-label={`Nota ${i}`}
                aria-pressed={selectedScore === i}
              >
                {i}
              </button>
            ))}
          </div>

          {/* Scale labels */}
          <div className="flex justify-between mt-1 px-0.5">
            <span className="text-xs text-gray-400">Muito insatisfeito</span>
            <span className="text-xs text-gray-400">Muito satisfeito</span>
          </div>
        </div>

        {/* Comment */}
        <div className="mt-5">
          <label htmlFor="nps-comment" className="block text-sm font-medium text-gray-700 mb-1.5">
            Comentário <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <textarea
            id="nps-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            disabled={status === 'submitting'}
            rows={3}
            maxLength={500}
            placeholder="Conte-nos mais sobre sua experiência..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:opacity-50 resize-none"
          />
          <p className="text-right text-xs text-gray-400 mt-0.5">{comment.length}/500</p>
        </div>

        {/* Error message */}
        {status === 'error' && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={selectedScore === null || status === 'submitting'}
          className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {status === 'submitting' ? 'Enviando...' : 'Enviar avaliação'}
        </button>
      </div>
    </div>
  );
}
